import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { Modal } from '@creditornot/cbt-modal';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ReportMetric } from 'apis/merchant-gateway/insights';
import { telemetryBusinessInsightsV2ReportBuilderChooseDataOptions } from 'modules/insights-v2/telemetry';

import { MetricsDetails } from './MetricsDetails';
import { MetricsModalContentForm } from './MetricsModalContentForm';
import { SupportableReportMetric } from './types';
import { isMetricSupported } from './utils';
import ReportFormItem from '../../core/ReportFormItem';
import { InsightReportForm } from '../../types';

type MetricsFormItemProps = {
  supportedMetrics: ReportMetric[];
  isOnlyPickerEnabledVenues: boolean;
  isOnlyWoltPlusVenuesSelected: boolean;
};

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MetricsFormItem = ({
  supportedMetrics,
  isOnlyPickerEnabledVenues,
  isOnlyWoltPlusVenuesSelected,
}: MetricsFormItemProps) => {
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
  const { formatMessage } = useIntl();
  const form = useForm<InsightReportForm>();
  const formState = form.getState();
  const formValues = formState.values;

  const handleUpdate = (values: InsightReportForm) => {
    form.change('metrics', values.metrics);
    telemetryBusinessInsightsV2ReportBuilderChooseDataOptions(values.metrics);
    handleClose();
  };

  const handleClose = () => {
    setIsMetricsModalOpen(false);
  };

  const supportableMetrics: SupportableReportMetric[] = useMemo(
    () =>
      supportedMetrics.map(metric => ({
        ...metric,
        isSupported: isMetricSupported(metric, isOnlyPickerEnabledVenues, isOnlyWoltPlusVenuesSelected),
      })),
    [supportedMetrics, isOnlyPickerEnabledVenues, isOnlyWoltPlusVenuesSelected],
  );

  return (
    <>
      <ReportFormItem
        action={
          <Button variant="secondary" size="small" onClick={() => setIsMetricsModalOpen(true)}>
            <FormattedMessage id="insights.reports.data-options.action" />
          </Button>
        }
        title={<FormattedMessage id="insights.reports.data-options.question" />}
        value={
          <Section>
            <MetricsDetails supportedMetrics={supportableMetrics} selectedMetrics={formValues.metrics ?? []} />
            <Alert variant="info" size="small">
              <FormattedMessage id="insights.reports.data-options.info" />
            </Alert>
            {formState.submitFailed && formState.errors?.metrics && (
              <Alert variant="error" size="small">
                {formatMessage({ id: formState.errors.metrics.id })}
              </Alert>
            )}
          </Section>
        }
      />
      <Modal open={isMetricsModalOpen} onDismiss={handleClose} disableContentPadding sizePreset="centered:large">
        <MetricsModalContentForm
          supportedMetrics={supportableMetrics}
          onUpdate={handleUpdate}
          onClose={handleClose}
          selectedMetrics={formValues.metrics ?? []}
        />
      </Modal>
    </>
  );
};
